/**
 * CA HT jour par jour, une courbe par entrée d'un classement — les dix
 * premiers pays (« CA HT par jour et par pays ») ou sites, produits.
 *
 * Un seul axe (des euros HT pour tous). Couleur par RANG dans le tableau, dans
 * l'ordre fixe de la palette : la première entrée garde la première couleur.
 *
 * Un jour sans commande ni remboursement pour une entrée n'a PAS de point :
 * dix courbes posées sur zéro se confondraient au pied du graphique. La courbe
 * s'interrompt, un point isolé reste visible. Par semaine au-delà de deux mois.
 */

import { useMemo } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { formatDayLong, formatDayShort, formatMoney, formatMoneyCompact } from '../lib/format';
import { bucketSizeFor } from '../lib/sales';
import { AXIS_STYLE, CATEGORICAL_COLORS, COLORS, LINE_WIDTH, OTHER_COLOR } from '../lib/theme';
import type { DataColumn } from '../types';
import { ChartCard } from './Card';
import { ChartLegend } from './ChartTooltip';
import { useChartMarkers } from '../hooks/useChartMarkers';
import { MarkerStrip } from './MarkerStrip';

type Row = { date: string } & Record<string, number | null | string>;

interface Series {
  key: string;
  label: string;
  color: string;
}

function bucketLabel(date: string, bucketSize: number): string {
  if (bucketSize === 1) return formatDayLong(date);
  return `${bucketSize === 7 ? 'Semaine' : '30 jours'} à partir du ${formatDayLong(date)}`;
}

function RankedTooltip({
  active,
  payload,
  series,
  bucketSize,
  currency,
}: {
  active?: boolean;
  payload?: Array<{ payload?: Row }>;
  series: readonly Series[];
  bucketSize: number;
  currency: string;
}) {
  const row = payload?.[0]?.payload;
  if (!active || !row) return null;
  const present = series
    .map((s) => ({ ...s, value: row[s.key] }))
    .filter((s): s is Series & { value: number } => typeof s.value === 'number')
    .sort((a, b) => b.value - a.value);
  return (
    <div className="tooltip" role="tooltip">
      <p className="tooltip__title">{bucketLabel(row.date, bucketSize)}</p>
      {present.length === 0 ? (
        <p className="tooltip__title">aucune vente ce jour-là</p>
      ) : (
        present.map((s) => (
          <div className="tooltip__row" key={s.key}>
            <span className="legend__key" style={{ background: s.color }} aria-hidden="true" />
            <span className="tooltip__name">{s.label}</span>
            <span className="tooltip__value">{formatMoney(s.value, currency)}</span>
          </div>
        ))
      )}
    </div>
  );
}


/** Marqueurs « ventes » : impact lu sur le total des courbes affichées. */
const MARKER_CONFIG = {
  value: (row: Row) =>
    Object.entries(row).reduce((sum, [key, value]) => (key !== 'date' && typeof value === 'number' ? sum + value : sum), 0),
  mode: 'level',
  unit: 'count',
  higherIsBetter: true,
  scope: 'sales',
} as const;

export function RankedTrendChart({
  title,
  noun,
  dates,
  keys,
  daily,
  nameOf,
  currency,
}: {
  title: string;
  /** « pays », « sites » — pour le sous-titre. */
  noun: string;
  /** Tous les jours de la période, dans l'ordre. */
  dates: readonly string[];
  /** Entrées, dans l'ordre du tableau (la première = la plus grosse part). `null` : inconnu. */
  keys: ReadonlyArray<string | null>;
  daily: ReadonlyArray<{ date: string; key: string | null; netRevenueExclTax: number }>;
  nameOf: (key: string | null) => string;
  currency: string;
}) {
  const series = useMemo<Series[]>(
    () =>
      keys.map((key, index) => ({
        key: `k_${key ?? 'none'}`,
        label: nameOf(key),
        color: CATEGORICAL_COLORS[index] ?? OTHER_COLOR,
      })),
    [keys, nameOf],
  );
  const bucketSize = bucketSizeFor(dates.length);

  const rows = useMemo<Row[]>(() => {
    const byKey = new Map<string, number>();
    for (const point of daily) byKey.set(`${point.date}|${point.key ?? 'none'}`, point.netRevenueExclTax);
    const out: Row[] = [];
    for (let start = 0; start < dates.length; start += bucketSize) {
      const slice = dates.slice(start, start + bucketSize);
      const first = slice[0];
      if (!first) continue;
      const row: Row = { date: first };
      keys.forEach((key, index) => {
        let sum: number | null = null;
        for (const date of slice) {
          const value = byKey.get(`${date}|${key ?? 'none'}`);
          if (value !== undefined) sum = (sum ?? 0) + value;
        }
        const column = series[index]?.key;
        if (column) row[column] = sum === null ? null : Math.round(sum * 100) / 100;
      });
      out.push(row);
    }
    return out;
  }, [dates, keys, daily, bucketSize, series]);

  const markers = useChartMarkers(rows, MARKER_CONFIG);

  const columns: ReadonlyArray<DataColumn<Row>> = [
    { key: 'date', header: bucketSize === 1 ? 'Jour' : 'À partir du', render: (row) => formatDayLong(row.date) },
    ...series.map((s) => ({
      key: s.key,
      header: s.label,
      numeric: true,
      render: (row: Row) => (typeof row[s.key] === 'number' ? formatMoney(row[s.key] as number, currency) : '—'),
    })),
  ];

  const axisTick = { fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize };
  const granularity = bucketSize === 1 ? 'par jour' : bucketSize === 7 ? 'par semaine' : 'par tranche de 30 jours';

  return (
    <ChartCard
      footer={<MarkerStrip items={markers.items} />}
      title={title}
      subtitle={`CA HT des ${series.length} premiers ${noun}, ${granularity} — pas de point un jour sans vente`}
      className="chart-grid__wide"
      rows={series.length > 0 ? rows : []}
      columns={columns}
      emptyMessage="Aucune vente sur cette période."
      chartHeight={360}
    >
      <ChartLegend entries={series.map((s) => ({ label: s.label, color: s.color }))} />
      <ResponsiveContainer width="100%" height="80%">
        <LineChart data={rows} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} vertical={false} />
          <XAxis dataKey="date" tickFormatter={formatDayShort} tick={axisTick} axisLine={{ stroke: COLORS.grid }} tickLine={false} minTickGap={32} />
          <YAxis tickFormatter={(value: number) => formatMoneyCompact(value, currency)} tick={axisTick} axisLine={false} tickLine={false} width={60} />
          <Tooltip
            cursor={{ stroke: COLORS.textMuted, strokeWidth: 1 }}
            content={<RankedTooltip series={series} bucketSize={bucketSize} currency={currency} />}
          />
          {markers.lines}
          {/* Dernière entrée d'abord : la première, en tête, se dessine par-dessus. */}
          {[...series].reverse().map((s) => (
            <Line
              key={s.key}
              type="monotone"
              dataKey={s.key}
              name={s.label}
              stroke={s.color}
              strokeWidth={LINE_WIDTH}
              dot={{ r: 4, fill: s.color, stroke: COLORS.surface, strokeWidth: 2 }}
              activeDot={{ r: 5, fill: s.color, stroke: COLORS.surface, strokeWidth: 2 }}
              connectNulls={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
